import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Observable, of, Subject } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';

import { AiKnowledgeI18nModule } from '../../assets/i18n/ai-knowledge-i18n.module';
import { KnowledgeBaseGrantSdkService } from '../knowledge-base-grant-sdk.service';
import {
  ACCOUNT_GROUPS_LOOKUP_TOKEN,
  CAN_SHARE_WITH_PARTNERS_TOKEN,
  IS_ADMIN_SURFACE_TOKEN,
  KNOWLEDGE_GRANTS_ENABLED_TOKEN,
  MULTI_LOCATION_GROUPS_LOOKUP_TOKEN,
  PARTNER_ID_TOKEN,
  PARTNERS_LOOKUP_TOKEN,
} from '../../tokens';
import {
  GrantDependentSummary,
  KnowledgeBaseGrant,
  KnowledgeBaseSharingDialogData,
  TargetOption,
  targetIdentityKey,
} from '../types';

/**
 * Target kind being picked. Availability is config-driven — each appears only when its lookup is wired and/or the surface
 * matches. Admin Center (`isAdminSurface`) offers the four global types — `partner`, `allAccountGroupsUnderPartner`,
 * `allPartners`, `allAccountGroupsGlobal`; partner/account-group surfaces keep `account` / `mlGroup` / `allAccountGroups`.
 */
type TargetType =
  | 'account'
  | 'mlGroup'
  | 'partner'
  | 'allAccountGroups'
  | 'allAccountGroupsUnderPartner'
  | 'allPartners'
  | 'allAccountGroupsGlobal';

interface BlockedRevokeState {
  grantId: string;
  dependents: GrantDependentSummary;
  // Which cleanup affordance is awaiting a confirm tap.
  pendingConfirm: 'apps' | 'downstream' | null;
}

@Component({
  selector: 'ai-knowledge-management-dialog',
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDialogModule,
    MatDividerModule,
    GalaxyFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatTooltipModule,
    GalaxyLoadingSpinnerModule,
    TranslateModule,
    AiKnowledgeI18nModule,
  ],
  templateUrl: './knowledge-base-sharing-dialog.component.html',
  styleUrls: ['./knowledge-base-sharing-dialog.component.scss'],
})
export class KnowledgeBaseSharingDialogComponent implements OnInit {
  protected readonly data = inject<KnowledgeBaseSharingDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<KnowledgeBaseSharingDialogComponent>);
  private readonly grants = inject(KnowledgeBaseGrantSdkService);
  private readonly snackbar = inject(SnackbarService);
  private readonly translate = inject(TranslateService);

  private readonly partnerId$ = inject(PARTNER_ID_TOKEN);
  private readonly canShareWithPartners$ = inject(CAN_SHARE_WITH_PARTNERS_TOKEN);
  private readonly isAdminSurface$ = inject(IS_ADMIN_SURFACE_TOKEN);
  private readonly grantsEnabled$ = inject(KNOWLEDGE_GRANTS_ENABLED_TOKEN);
  private readonly accountGroupsLookup = inject(ACCOUNT_GROUPS_LOOKUP_TOKEN);
  private readonly mlGroupsLookup = inject(MULTI_LOCATION_GROUPS_LOOKUP_TOKEN);
  private readonly partnersLookup = inject(PARTNERS_LOOKUP_TOKEN);

  readonly isLoading = signal(true);
  readonly isApplying = signal(false);
  /** grantId currently being revoked (dependents check in flight or revoke RPC in flight). */
  readonly revokingGrantId = signal<string | null>(null);
  /** When non-null, the current-shares row for `grantId` renders in blocked-state mode. */
  readonly blockedRevoke = signal<BlockedRevokeState | null>(null);

  readonly currentGrants = signal<KnowledgeBaseGrant[]>([]);
  /** Set when listGrantsForKB failed during init; drives an inline error in "Current shares" (the modal still opens for adding). */
  readonly currentGrantsLoadError = signal<string | null>(null);
  readonly partnerIdValue = signal<string>('');
  readonly canShareWithPartners = signal<boolean>(false);
  readonly isAdminSurface = signal<boolean>(false);

  /** Async-loaded options for the picker, keyed by target type. */
  readonly availableOptions = signal<TargetOption[]>([]);
  readonly isLookupInFlight = signal<boolean>(false);

  readonly targetType = signal<TargetType>('account');
  readonly search = signal('');
  /** identityKey → TargetOption — dedup and order-stable. */
  readonly selected = signal<Map<string, TargetOption>>(new Map());

  readonly selectedCount = computed(() => this.selected().size);

  /** Dropdown options, from the wired lookups plus the admin flag. Empty (degrades to a no-op) when a surface wires no lookup. `label` is an i18n key, resolved by the `translate` pipe in the template. */
  readonly targetTypeOptions = computed<Array<{ value: TargetType; label: string }>>(() => {
    const opts: Array<{ value: TargetType; label: string }> = [];
    if (this.accountGroupsLookup) {
      opts.push({ value: 'account', label: 'KNOWLEDGE_SHARING.SHARE.TARGET_TYPE_ACCOUNT' });
    }
    if (this.mlGroupsLookup) {
      opts.push({ value: 'mlGroup', label: 'KNOWLEDGE_SHARING.SHARE.TARGET_TYPE_ML_GROUP' });
    }
    if (this.partnersLookup) {
      opts.push({ value: 'partner', label: 'KNOWLEDGE_SHARING.SHARE.TARGET_TYPE_PARTNER' });
    }
    // Admin-only: pick a partner, share to every account group under it (Partner namespace + ALL_AGS). Reuses the partners picker.
    if (this.isAdminSurface() && this.partnersLookup) {
      opts.push({
        value: 'allAccountGroupsUnderPartner',
        label: 'KNOWLEDGE_SHARING.SHARE.TARGET_TYPE_ALL_ACCOUNTS_UNDER_PARTNER',
      });
    }
    // The caller's own "all accounts under this partner" option is partner-scoped — meaningless on the admin surface, where
    // there is no single owning partner (the admin picks one via allAccountGroupsUnderPartner instead).
    if (this.partnerIdValue() && !this.isAdminSurface()) {
      opts.push({ value: 'allAccountGroups', label: 'KNOWLEDGE_SHARING.SHARE.TARGET_TYPE_ALL_ACCOUNTS' });
    }
    if (this.canShareWithPartners()) {
      opts.push({ value: 'allPartners', label: 'KNOWLEDGE_SHARING.SHARE.TARGET_TYPE_ALL_PARTNERS' });
    }
    // Admin-only: broadcast to every account group across every partner (admin namespace + ALL_AGS).
    if (this.isAdminSurface()) {
      opts.push({
        value: 'allAccountGroupsGlobal',
        label: 'KNOWLEDGE_SHARING.SHARE.TARGET_TYPE_ALL_ACCOUNT_GROUPS_GLOBAL',
      });
    }
    return opts;
  });

  /** Search makes sense only for free-text list types (the lookup-backed ones). */
  readonly showSearch = computed(() => {
    const t = this.targetType();
    return (
      t === 'account' || t === 'mlGroup' || t === 'partner' || t === 'allAccountGroupsUnderPartner'
    );
  });

  /** identityKeys of grants that already exist — used to disable rows in the picker. */
  readonly currentTargetKeys = computed(
    () => new Set(this.currentGrants().map((g) => g.target.identityKey)),
  );

  /** Debounces the lookup RPC (300ms); the `search` signal still updates synchronously so the input stays responsive. */
  private readonly searchInput$ = new Subject<void>();
  /** Monotonic token so a lookup that resolves after a newer one (e.g. rapid target-type switch) can't clobber the current list. */
  private lookupSeq = 0;

  constructor() {
    this.searchInput$.pipe(debounceTime(300), takeUntilDestroyed()).subscribe(() => {
      if (this.showSearch()) {
        void this.refreshAvailableOptions();
      }
    });
  }

  async ngOnInit(): Promise<void> {
    this.isLoading.set(true);
    try {
      // Defense in depth: close immediately if the grants flag is off, in case an upstream affordance bypassed the page-level gate.
      const grantsOn = (await firstValueFrom(this.grantsEnabled$).catch(() => false)) ?? false;
      if (!grantsOn) {
        this.dialogRef.close();
        return;
      }
      // listGrantsForKB failure is non-fatal — the dialog still opens for adding grants, with the error shown in "Current shares".
      const [grants, partnerId, canShareWithPartners, isAdminSurface] = await Promise.all([
        this.grants.listGrantsForKB(this.data.kbId).catch((err: unknown) => {
          console.error('[knowledge-management] listGrantsForKB failed', err);
          this.currentGrantsLoadError.set('KNOWLEDGE_SHARING.SHARE.LOAD_SHARES_ERROR');
          return [] as KnowledgeBaseGrant[];
        }),
        firstValueFrom(this.partnerId$).catch(() => ''),
        firstValueFrom(this.canShareWithPartners$).catch(() => false),
        firstValueFrom(this.isAdminSurface$).catch(() => false),
      ]);
      this.currentGrants.set(grants);
      this.partnerIdValue.set(partnerId ?? '');
      this.canShareWithPartners.set(canShareWithPartners ?? false);
      this.isAdminSurface.set(isAdminSurface ?? false);

      // Pick the first available target type as the default.
      const firstOpt = this.targetTypeOptions()[0];
      if (firstOpt) {
        this.targetType.set(firstOpt.value);
        await this.refreshAvailableOptions();
      }
    } finally {
      this.isLoading.set(false);
    }
  }

  /** Reload the picker list when target type or search query changes. */
  private async refreshAvailableOptions(): Promise<void> {
    const seq = ++this.lookupSeq;
    const type = this.targetType();
    const query = this.search();
    let stream: Observable<TargetOption[]> = of([]);
    switch (type) {
      case 'account':
        if (!this.accountGroupsLookup) {
          throw new Error(
            '[ai-knowledge-management-dialog] accountGroupsLookup is not configured. ' +
              'A consumer wired `account` as a target type without providing the lookup function.',
          );
        }
        stream = this.accountGroupsLookup(query);
        break;
      case 'mlGroup':
        if (!this.mlGroupsLookup) {
          throw new Error(
            '[ai-knowledge-management-dialog] multiLocationGroupsLookup is not configured.',
          );
        }
        stream = this.mlGroupsLookup(query);
        break;
      case 'partner':
        if (!this.partnersLookup) {
          throw new Error(
            '[ai-knowledge-management-dialog] partnersLookup is not configured.',
          );
        }
        stream = this.partnersLookup(query);
        break;
      case 'allAccountGroupsUnderPartner':
        if (!this.partnersLookup) {
          throw new Error(
            '[ai-knowledge-management-dialog] partnersLookup is not configured. ' +
              'allAccountGroupsUnderPartner reuses the partners picker.',
          );
        }
        // Same partner picker as `partner`, but each pick targets every account group under that partner (ALL_AGS) rather than the partner itself.
        stream = this.partnersLookup(query).pipe(
          map((partners) => partners.map((p) => toAllAccountGroupsUnderPartner(p))),
        );
        break;
      case 'allAccountGroups':
        stream = of([await this.synthesizedAllAccountGroupsTarget()]);
        break;
      case 'allPartners':
        stream = of([await this.synthesizedAllPartnersTarget()]);
        break;
      case 'allAccountGroupsGlobal':
        stream = of([await this.synthesizedAllAccountGroupsGlobalTarget()]);
        break;
    }
    // Clear stale options first so a slow or failing lookup can't leave the previous target type's rows against the new selection.
    this.availableOptions.set([]);
    this.isLookupInFlight.set(true);
    try {
      const opts = await firstValueFrom(stream);
      // Bail if a newer lookup superseded us while awaiting — its result is the one that matches the current selection.
      if (seq === this.lookupSeq) this.availableOptions.set(opts);
    } catch (err: unknown) {
      // Keep rendering — the empty list is the correct degraded UX. No snackbar: per-keystroke lookup churn would be noisy.
      console.error('[knowledge-management] target lookup failed', err);
    } finally {
      // Only the latest lookup owns the in-flight flag; a superseded one clearing it would hide the active spinner.
      if (seq === this.lookupSeq) this.isLookupInFlight.set(false);
    }
  }

  isSelected(target: TargetOption): boolean {
    return this.selected().has(target.identityKey);
  }

  toggleTarget(target: TargetOption, checked: boolean): void {
    this.selected.update((map) => {
      const next = new Map(map);
      if (checked) {
        next.set(target.identityKey, target);
      } else {
        next.delete(target.identityKey);
      }
      return next;
    });
  }

  async setTargetType(type: TargetType): Promise<void> {
    this.targetType.set(type);
    this.search.set('');
    // Clear pending picks: the picker only renders the current type's options, so a selection carried over from a
    // previous type would be committed by apply() while invisible on screen — an easy way to broadcast a grant by
    // accident (e.g. a platform-wide share left checked after switching back to single-partner). Each target type
    // is selected and applied on its own.
    this.selected.set(new Map());
    await this.refreshAvailableOptions();
  }

  onSearchChange(value: string): void {
    this.search.set(value);
    this.searchInput$.next();
  }

  /** Server-authoritative revoke. On FailedPrecondition the SDK returns `{ dependents }` and the row enters blocked-state mode; rows are removed only after the RPC succeeds. */
  async revokeGrant(grant: KnowledgeBaseGrant): Promise<void> {
    this.revokingGrantId.set(grant.grantId);
    try {
      const result = await this.grants.revokeGrant(this.data.kbId, grant.grantId);
      // `'dependents' in result` is the union narrow ngc reliably honors; `if (result.ok)` across the await fails strict template type-check in some configs.
      if ('dependents' in result) {
        this.blockedRevoke.set({
          grantId: grant.grantId,
          dependents: result.dependents,
          pendingConfirm: null,
        });
      } else {
        this.currentGrants.update((list) => list.filter((g) => g.grantId !== grant.grantId));
        if (this.blockedRevoke()?.grantId === grant.grantId) {
          this.blockedRevoke.set(null);
        }
      }
    } catch (err) {
      console.error('[knowledge-management] revokeGrant failed', err);
      this.snackbar.openErrorSnack('KNOWLEDGE_SHARING.SHARE.REVOKE_ERROR');
    } finally {
      this.revokingGrantId.set(null);
    }
  }

  /** User clicks one of the cleanup affordances — first click flags pending confirm. */
  flagConfirm(kind: 'apps' | 'downstream'): void {
    this.blockedRevoke.update((b) => (b ? { ...b, pendingConfirm: kind } : null));
  }

  cancelConfirm(): void {
    this.blockedRevoke.update((b) => (b ? { ...b, pendingConfirm: null } : null));
  }

  cancelBlockedRevoke(): void {
    this.blockedRevoke.set(null);
  }

  async confirmDetachApps(grant: KnowledgeBaseGrant): Promise<void> {
    const ns = { id: grant.target.namespaceId, type: grant.target.namespaceType };
    this.revokingGrantId.set(grant.grantId);
    try {
      await this.grants.removeKnowledgeBaseFromAppsInNamespace(this.data.kbId, ns);
      this.blockedRevoke.update((b) =>
        b
          ? {
              ...b,
              pendingConfirm: null,
              dependents: { ...b.dependents, appAssociationCount: 0, sampleAppAssociations: [] },
            }
          : null,
      );
      await this.maybeRetryRevoke(grant);
    } catch (err) {
      console.error('[knowledge-management] removeKnowledgeBaseFromAppsInNamespace failed', err);
      this.snackbar.openErrorSnack('KNOWLEDGE_SHARING.SHARE.DETACH_APPS_ERROR');
    } finally {
      this.revokingGrantId.set(null);
    }
  }

  async confirmRevokeDownstream(grant: KnowledgeBaseGrant): Promise<void> {
    const ns = { id: grant.target.namespaceId, type: grant.target.namespaceType };
    this.revokingGrantId.set(grant.grantId);
    try {
      await this.grants.revokeIssuedGrants(this.data.kbId, ns);
      this.blockedRevoke.update((b) =>
        b
          ? {
              ...b,
              pendingConfirm: null,
              dependents: { ...b.dependents, downstreamGrantCount: 0, sampleDownstreamGrants: [] },
            }
          : null,
      );
      await this.maybeRetryRevoke(grant);
    } catch (err) {
      console.error('[knowledge-management] revokeIssuedGrants failed', err);
      this.snackbar.openErrorSnack('KNOWLEDGE_SHARING.SHARE.REVOKE_DOWNSTREAM_ERROR');
    } finally {
      this.revokingGrantId.set(null);
    }
  }

  /** Once both cleanup affordances are drained, retry the revoke; a fresh FailedPrecondition just re-renders blocked state via revokeGrant. */
  private async maybeRetryRevoke(grant: KnowledgeBaseGrant): Promise<void> {
    const b = this.blockedRevoke();
    if (!b) return;
    if (b.dependents.downstreamGrantCount === 0 && b.dependents.appAssociationCount === 0) {
      // Skip the revokingGrantId guard — revokeGrant manages it itself.
      await this.revokeGrant(grant);
    }
  }

  async apply(): Promise<void> {
    if (this.selectedCount() === 0) return;
    this.isApplying.set(true);
    try {
      const targets = Array.from(this.selected().values());
      // allSettled (not all): a partial failure must still commit the grants that succeeded — otherwise they
      // live on the backend but not in currentGrants, and a retry over the whole selection re-creates them.
      const results = await Promise.allSettled(
        targets.map((t) => this.grants.createGrant(this.data.kbId, t)),
      );

      const created: KnowledgeBaseGrant[] = [];
      const failed: TargetOption[] = [];
      results.forEach((result, i) => {
        if (result.status === 'fulfilled') {
          created.push(result.value);
        } else {
          failed.push(targets[i]);
          console.error('[knowledge-management] createGrant failed', result.reason);
        }
      });

      if (created.length > 0) {
        this.currentGrants.update((list) => [...list, ...created]);
      }
      // Keep only the failures selected, so a retry re-creates just those — not the ones already granted.
      this.selected.set(new Map(failed.map((t) => [t.identityKey, t])));

      if (failed.length > 0) {
        const message = await firstValueFrom(
          created.length > 0
            ? this.translate.get('KNOWLEDGE_SHARING.SHARE.SHARE_PARTIAL_ERROR', {
                shared: created.length,
                failed: failed.length,
              })
            : this.translate.get('KNOWLEDGE_SHARING.SHARE.SHARE_ERROR'),
        );
        this.snackbar.openErrorSnack(message);
      }

      // Stay open for batch sharing per the plan; refresh so newly-granted targets show as disabled.
      await this.refreshAvailableOptions();
    } finally {
      this.isApplying.set(false);
    }
  }

  close(): void {
    this.dialogRef.close({ grants: this.currentGrants() });
  }

  trackTargetKey = (_: number, t: TargetOption): string => t.identityKey;
  trackGrantId = (_: number, g: KnowledgeBaseGrant): string => g.grantId;

  /** Material icon name appropriate for a target's namespace type. */
  targetIconFor(namespaceType: TargetOption['namespaceType']): string {
    switch (namespaceType) {
      case 'AccountGroup':
        return 'storefront';
      case 'MultiLocationGroup':
        return 'hub';
      case 'Partner':
        return 'business';
      case 'Global':
        return 'public';
      default:
        return 'share';
    }
  }

  private async synthesizedAllAccountGroupsTarget(): Promise<TargetOption> {
    const partnerId = this.partnerIdValue();
    const t = await firstValueFrom(
      this.translate.get([
        'KNOWLEDGE_SHARING.SHARE.ALL_ACCOUNTS_NAME',
        'KNOWLEDGE_SHARING.SHARE.ALL_ACCOUNTS_SUBTITLE',
      ]),
    );
    return {
      identityKey: targetIdentityKey('Partner', partnerId, 'all_ags'),
      namespaceId: partnerId,
      namespaceType: 'Partner',
      scope: 'all_ags',
      displayName: t['KNOWLEDGE_SHARING.SHARE.ALL_ACCOUNTS_NAME'],
      subtitle: t['KNOWLEDGE_SHARING.SHARE.ALL_ACCOUNTS_SUBTITLE'],
    };
  }

  private async synthesizedAllPartnersTarget(): Promise<TargetOption> {
    const t = await firstValueFrom(
      this.translate.get([
        'KNOWLEDGE_SHARING.SHARE.ALL_PARTNERS_NAME',
        'KNOWLEDGE_SHARING.SHARE.ALL_PARTNERS_SUBTITLE',
      ]),
    );
    return {
      identityKey: targetIdentityKey('Global', '*', 'all_partners'),
      namespaceId: '*',
      namespaceType: 'Global',
      scope: 'all_partners',
      displayName: t['KNOWLEDGE_SHARING.SHARE.ALL_PARTNERS_NAME'],
      subtitle: t['KNOWLEDGE_SHARING.SHARE.ALL_PARTNERS_SUBTITLE'],
    };
  }

  /** "All account groups (platform-wide)" — every AG across every partner. Admin namespace + ALL_AGS, distinct from the all-partners broadcast. */
  private async synthesizedAllAccountGroupsGlobalTarget(): Promise<TargetOption> {
    const t = await firstValueFrom(
      this.translate.get([
        'KNOWLEDGE_SHARING.SHARE.ALL_ACCOUNT_GROUPS_GLOBAL_NAME',
        'KNOWLEDGE_SHARING.SHARE.ALL_ACCOUNT_GROUPS_GLOBAL_SUBTITLE',
      ]),
    );
    return {
      identityKey: targetIdentityKey('Global', '*', 'all_ags'),
      namespaceId: '*',
      namespaceType: 'Global',
      scope: 'all_ags',
      displayName: t['KNOWLEDGE_SHARING.SHARE.ALL_ACCOUNT_GROUPS_GLOBAL_NAME'],
      subtitle: t['KNOWLEDGE_SHARING.SHARE.ALL_ACCOUNT_GROUPS_GLOBAL_SUBTITLE'],
    };
  }
}

/**
 * Re-scopes a partner pick (from the partners lookup, which returns `self`-scoped Partner targets) to "all account groups
 * under this partner" — Partner namespace + ALL_AGS. The identityKey carries the scope, so the same partner can be granted
 * both `self` and `all_ags` without colliding.
 */
function toAllAccountGroupsUnderPartner(partner: TargetOption): TargetOption {
  return {
    ...partner,
    identityKey: targetIdentityKey('Partner', partner.namespaceId, 'all_ags'),
    namespaceType: 'Partner',
    scope: 'all_ags',
  };
}
